import { type DragEvent, useState } from "react";
import {
  Type,
  Database,
  MapPin,
  Scale,
  GitMerge,
  Ban,
  Search,
  ListChecks,
  FunctionSquare,
  Pin,
  PinOff,
  X,
  CheckCircle,
  XCircle,
  HelpCircle,
  Workflow,
  BarChart3,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface DecisionToolboxProps {
  isOpen: boolean;
  onClose: () => void;
  isPinned: boolean;
  onPinToggle: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const NODE_CATEGORIES = [
  {
    name: "Values",
    description: "Data sources",
    nodes: [
      {
        type: "literal",
        label: "Literal",
        description: "Constant value (string, number, boolean)",
        icon: Type,
        color: "bg-indigo-100 text-indigo-600 border-indigo-200",
      },
      {
        type: "field",
        label: "Fact Reference",
        description: "Reference a fact from the input",
        icon: Database,
        color: "bg-emerald-100 text-emerald-600 border-emerald-200",
      },
      {
        type: "context_ref",
        label: "Context Reference",
        description: "Reference a context value",
        icon: MapPin,
        color: "bg-lime-100 text-lime-600 border-lime-200",
      },
    ],
  },
  {
    name: "Logic",
    description: "Boolean operations",
    nodes: [
      {
        type: "binary",
        label: "AND / OR",
        description: "Combine two conditions",
        icon: GitMerge,
        color: "bg-cyan-100 text-cyan-600 border-cyan-200",
      },
      {
        type: "unary",
        label: "NOT",
        description: "Negate a condition",
        icon: Ban,
        color: "bg-red-100 text-red-600 border-red-200",
      },
      {
        type: "is_true",
        label: "Is True",
        description: "Check if expression is true",
        icon: CheckCircle,
        color: "bg-green-100 text-green-600 border-green-200",
      },
      {
        type: "is_false",
        label: "Is False",
        description: "Check if expression is false",
        icon: XCircle,
        color: "bg-rose-100 text-rose-600 border-rose-200",
      },
      {
        type: "is_unknown",
        label: "Is Unknown",
        description: "Check if expression is unknown",
        icon: HelpCircle,
        color: "bg-amber-100 text-amber-600 border-amber-200",
      },
    ],
  },
  {
    name: "Comparison",
    description: "Compare values",
    nodes: [
      {
        type: "comparison",
        label: "Compare",
        description: "==, !=, <, <=, >, >=",
        icon: Scale,
        color: "bg-orange-100 text-orange-600 border-orange-200",
      },
      {
        type: "existence",
        label: "Exists",
        description: "Check if field is set",
        icon: Search,
        color: "bg-violet-100 text-violet-600 border-violet-200",
      },
      {
        type: "membership",
        label: "In Set",
        description: "Check if value is in a set",
        icon: ListChecks,
        color: "bg-teal-100 text-teal-600 border-teal-200",
      },
    ],
  },
  {
    name: "Decision",
    description: "Branching & scoring",
    nodes: [
      {
        type: "case",
        label: "Case",
        description: "If/then/else branching",
        icon: Workflow,
        color: "bg-purple-100 text-purple-600 border-purple-200",
      },
      {
        type: "scorecard",
        label: "Scorecard",
        description: "Weighted factor scoring",
        icon: BarChart3,
        color: "bg-pink-100 text-pink-600 border-pink-200",
      },
    ],
  },
  {
    name: "Advanced",
    description: "Complex operations",
    nodes: [
      {
        type: "function",
        label: "Function",
        description: "Call a custom function",
        icon: FunctionSquare,
        color: "bg-amber-100 text-amber-600 border-amber-200",
      },
    ],
  },
];

export function DecisionToolbox({
  isOpen,
  onClose,
  isPinned,
  onPinToggle,
  onMouseEnter,
  onMouseLeave,
}: DecisionToolboxProps) {
  const [isDragging, setIsDragging] = useState(false);

  const onDragStart = (event: DragEvent, nodeType: string) => {
    event.dataTransfer.setData("application/reactflow-type", nodeType);
    event.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
  };

  const onDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      {isOpen && (
        <div
          className={cn(
            "flex h-full w-72 flex-col border-l border-slate-200 bg-white shadow-xl",
            !isPinned && "absolute right-0 top-0 z-20"
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900">
                Expression Nodes
              </h3>
              {!isPinned && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">
                  hover
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", isPinned && "bg-blue-50 text-blue-600")}
                onClick={onPinToggle}
                title={isPinned ? "Unpin toolbox" : "Pin toolbox"}
              >
                {isPinned ? (
                  <PinOff size={14} className={isPinned ? "text-blue-600" : "text-slate-500"} />
                ) : (
                  <Pin size={14} className="text-slate-500" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onClose}
                title="Close toolbox"
              >
                <X size={14} className="text-slate-500" />
              </Button>
            </div>
          </div>

          <div className="h-[calc(100%-52px)] space-y-4 overflow-y-auto p-3">
            {NODE_CATEGORIES.map((category) => (
              <div key={category.name}>
                <div className="mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {category.name}
                  </h4>
                  <p className="text-[10px] text-slate-400">
                    {category.description}
                  </p>
                </div>
                <div className="space-y-1.5">
                  {category.nodes.map((node) => {
                    const Icon = node.icon;
                    return (
                      <div
                        key={node.type}
                        draggable
                        onDragStart={(e) => onDragStart(e, node.type)}
                        onDragEnd={onDragEnd}
                        className={cn(
                          "cursor-grab rounded-lg border p-2 transition-all",
                          "hover:scale-[1.02] hover:shadow-md active:cursor-grabbing",
                          node.color
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Icon size={16} />
                          <div>
                            <div className="text-sm font-medium">{node.label}</div>
                            <div className="text-[10px] opacity-75">
                              {node.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="border-t border-slate-200 pt-4">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                How to use
              </h4>
              <ul className="space-y-1 text-xs text-slate-500">
                <li>• Drag nodes onto the canvas</li>
                <li>• Connect handles to build expressions</li>
                <li>• Click nodes to edit in the drawer</li>
                <li>• Pin the toolbox to keep it open</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function useDecisionToolbox() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      const timeout = setTimeout(() => setIsOpen(false), 300);
      setHoverTimeout(timeout);
    }
  };

  const handlePinToggle = () => {
    setIsPinned(!isPinned);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsPinned(false);
  };

  return {
    isOpen: isOpen || isPinned,
    isPinned,
    onOpen: () => setIsOpen(true),
    onClose: handleClose,
    onPinToggle: handlePinToggle,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };
}
